import React from 'react'
import axios from 'axios'
import {
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableBody,
  IconButton,
  Chip,
  Typography,
  Collapse,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import {
  ExpandMore,
  ExpandLess,
  Delete,
  Edit
} from '@material-ui/icons'
import ComponentBuilder from './ComponentBuilder'
import QuickPriceInput from './QuickPriceInput'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const coverageChip = (status) => {
  switch (status) {
    case 'covered':
      return { label: 'Covered', style: { backgroundColor: '#c8e6c9', color: '#2e7d32' } }
    case 'partial':
      return { label: 'Partial', style: { backgroundColor: '#fff9c4', color: '#f57f17' } }
    case 'missing':
      return { label: 'Missing', style: { backgroundColor: '#ffcdd2', color: '#c62828' } }
    case 'needs_review':
      return { label: 'Review', style: { backgroundColor: '#fff3e0', color: '#e65100' } }
    default:
      return { label: status || 'Unknown', style: {} }
  }
}

const rowBg = (status) => {
  switch (status) {
    case 'covered': return 'rgba(200, 230, 201, 0.15)'
    case 'missing': return 'rgba(255, 205, 210, 0.15)'
    case 'needs_review': return 'rgba(255, 243, 224, 0.2)'
    default: return 'transparent'
  }
}

const rateDisplay = (comp) => {
  const t = comp.costType
  if (t === 'labor' || t === 'equipment') {
    const pr = comp.productionRate || {}
    return `${comp.componentQty || 0} units / ${pr.adjustedRate || pr.baseRate || 0} units/hr = ${(pr.hours || 0).toFixed(1)} hrs x ${fmt(pr.hourlyRate || 0)}/hr`
  }
  if (t === 'material') {
    const mp = comp.materialPricing || {}
    const unitCost = mp.manualOverride || mp.unitCost || 0
    return `${comp.componentQty || 0} units x ${fmt(unitCost)}/unit`
  }
  // subcontract / disposal
  const sp = comp.subcontractPricing || {}
  return `${comp.componentQty || 0} units x ${fmt(sp.unitCost || 0)}/unit`
}

const typeColor = (t) => {
  switch (t) {
    case 'labor': return '#1976d2'
    case 'equipment': return '#7b1fa2'
    case 'material': return '#388e3c'
    case 'subcontract': return '#f57c00'
    case 'disposal': return '#5d4037'
    default: return '#757575'
  }
}

const LineItemRow = ({ item, project, url, onRefresh }) => {
  const [expanded, setExpanded] = React.useState(false)
  const [components, setComponents] = React.useState([])
  const [loadingComps, setLoadingComps] = React.useState(false)
  const [builderOpen, setBuilderOpen] = React.useState(false)
  const [editComp, setEditComp] = React.useState(null)
  const [deleteTarget, setDeleteTarget] = React.useState(null)
  const [deleteItemConfirm, setDeleteItemConfirm] = React.useState(false)

  const fetchComponents = () => {
    setLoadingComps(true)
    axios.get(`${url}/api/bid-estimation/line-items/${item._id}/components`).then(res => {
      setComponents(res.data)
      setLoadingComps(false)
    }).catch(err => {
      console.error(err)
      setLoadingComps(false)
    })
  }

  const handleToggle = () => {
    if (!expanded) fetchComponents()
    setExpanded(!expanded)
  }

  const handleDeleteItem = () => {
    axios.delete(`${url}/api/bid-estimation/line-items/${item._id}`).then(() => {
      setDeleteItemConfirm(false)
      onRefresh()
    }).catch(err => console.error(err))
  }

  const handleDeleteComp = () => {
    if (!deleteTarget) return
    axios.delete(`${url}/api/bid-estimation/components/${deleteTarget._id}`).then(() => {
      setDeleteTarget(null)
      fetchComponents()
      onRefresh()
    }).catch(err => console.error(err))
  }

  const handleCompSaved = () => {
    setBuilderOpen(false)
    setEditComp(null)
    fetchComponents()
    onRefresh()
  }

  const chip = coverageChip(item.coverageStatus)

  return (
    <>
      <TableRow style={{ backgroundColor: rowBg(item.coverageStatus) }}>
        <TableCell style={{ width: 40 }}>
          <IconButton size="small" onClick={handleToggle}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{item.itemNo}</TableCell>
        <TableCell>{item.description}</TableCell>
        <TableCell>{item.unit}</TableCell>
        <TableCell align="right">{(item.quantity || 0).toLocaleString()}</TableCell>
        <TableCell align="right">{fmt(item.unitCost)}</TableCell>
        <TableCell align="right">{fmt(item.totalSell)}</TableCell>
        <TableCell align="center">
          <Chip size="small" label={chip.label} style={chip.style} />
        </TableCell>
        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
          <IconButton size="small" onClick={() => setDeleteItemConfirm(true)}>
            <Delete fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expanded components section */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
                Components
              </Typography>

              {loadingComps ? (
                <Typography variant="body2" style={{ padding: 8 }}>Loading...</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Rate / Price</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right">Ext Cost</TableCell>
                      <TableCell align="right">Markup</TableCell>
                      <TableCell align="right">Sell Price</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {components.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" color="textSecondary">No components yet</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      components.map(comp => (
                        <TableRow key={comp._id}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={comp.costType}
                              style={{ backgroundColor: typeColor(comp.costType), color: '#fff', textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>{comp.description}</TableCell>
                          <TableCell align="right">{comp.componentQty}</TableCell>
                          <TableCell style={{ fontSize: '0.8rem' }}>{rateDisplay(comp)}</TableCell>
                          <TableCell align="right">{comp.productionRate?.hours?.toFixed(1) || '—'}</TableCell>
                          <TableCell align="right">{fmt(comp.extendedCost)}</TableCell>
                          <TableCell align="right">{(comp.markupPct || 0).toFixed(0)}%</TableCell>
                          <TableCell align="right">{fmt(comp.sellPrice)}</TableCell>
                          <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                            <IconButton size="small" onClick={() => { setEditComp(comp); setBuilderOpen(true) }}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setDeleteTarget(comp)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}

              <Box display="flex" alignItems="center" mt={1} mb={1} style={{ gap: 8 }}>
                <Button size="small" variant="outlined" color="primary" onClick={() => { setEditComp(null); setBuilderOpen(true) }}>
                  Add Component
                </Button>
                <QuickPriceInput lineItem={item} url={url} onSave={handleCompSaved} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <ComponentBuilder
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditComp(null) }}
        onSave={handleCompSaved}
        lineItem={item}
        project={project}
        url={url}
        editComponent={editComp}
      />

      {/* Delete component dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Component</DialogTitle>
        <DialogContent>
          <Typography>Delete "{deleteTarget?.description || deleteTarget?.costType}" component?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDeleteComp} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete line item dialog */}
      <Dialog open={deleteItemConfirm} onClose={() => setDeleteItemConfirm(false)}>
        <DialogTitle>Delete Line Item</DialogTitle>
        <DialogContent>
          <Typography>Delete "{item.description}"? This will also remove all its components.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteItemConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteItem} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default LineItemRow
